/**
 * 📅 연도 다중 선택 드롭다운 — 공용.
 *
 *   - 버튼 클릭으로 드롭다운 펼침. 외부 클릭 / Esc 로 닫힘.
 *   - 체크박스 다중 선택 + [전체 선택] / [전체 해제] 빠른 토글.
 *   - 영업진행 + 매입 목록 등 표 페이지에서 동일 UX.
 *
 * 부모가 관리:
 *   - availableYears: 데이터 있는 연도 (백엔드에서 distinct 추출)
 *   - selectedYears: 현재 선택된 연도 (Set)
 *   - onToggle/onSelectAll/onClearAll: 변경 콜백
 */

import { useEffect, useRef, useState } from 'react';

export interface YearDropdownProps {
  availableYears: string[];
  selectedYears: Set<string>;
  onToggle: (y: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  yearOn: (y: string) => boolean;
  allYearsOn: boolean;
  allYearsOff: boolean;
  /** 라벨에 표시할 prefix (기본 "📅 연도") */
  label?: string;
}

export function YearDropdown({
  availableYears,
  selectedYears,
  onToggle,
  onSelectAll,
  onClearAll,
  yearOn,
  allYearsOn,
  allYearsOff,
  label = '📅 연도',
}: YearDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } // 창까지 닫지 않게 (v2.231.0 · H-4)
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const labelText = allYearsOn
    ? '전체'
    : allYearsOff
      ? '없음'
      : `${selectedYears.size}개 선택`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`btn btn-sm ${open ? 'btn-outline' : 'btn-secondary'}`}
      >
        <span>{label}</span>
        <span className="text-xs text-slate-500">({labelText})</span>
        <span className="text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-300 rounded shadow-lg z-30 min-w-[200px] py-1">
          <div className="px-3 py-1.5 text-xs text-slate-500 border-b border-slate-100 flex justify-between items-center gap-2">
            <span>데이터 있는 연도</span>
            <div className="flex gap-2">
              {!allYearsOn && (
                <button
                  type="button"
                  onClick={onSelectAll}
                  className="text-blue-600 hover:underline text-xs"
                >
                  전체 선택
                </button>
              )}
              {!allYearsOff && (
                <button
                  type="button"
                  onClick={onClearAll}
                  className="text-slate-500 hover:underline text-xs"
                >
                  전체 해제
                </button>
              )}
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {availableYears.map((y) => {
              const on = yearOn(y);
              return (
                <label
                  key={y}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => onToggle(y)}
                    className="rounded"
                  />
                  <span>{y}년</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
